import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '$lib/components/ui/Button';
import { cn } from '$lib/lib/cn';

type Tone = 'danger' | 'warning';

export interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  body: ReactNode;
  confirmLabel: ReactNode;
  // Falls back to common.cancel when omitted.
  dismissLabel?: ReactNode;
  // Defaults to 'danger' so destructive intent reads loudly. Use 'warning'
  // for actions where the worst-case is recoverable (e.g. clearing filters).
  tone?: Tone;
  onConfirm: () => void;
  onDismiss: () => void;
  // Optional decoration on the title. Defaults to a tone-coloured triangle so
  // the icon matches the action severity without prop sprawl.
  icon?: ReactNode;
  'data-testid'?: string;
}

// Reusable destructive-action confirmation modal. Mirrors SaveSearchDialog's
// portal + backdrop + escape behaviour — the savings vs. inlining are mostly
// in copy + tone consistency for the next destructive flow we add.
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  dismissLabel,
  tone = 'danger',
  onConfirm,
  onDismiss,
  icon,
  'data-testid': testId
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onDismiss();
      }
    }
    document.addEventListener('keydown', onKey);
    // Focus the confirm button on the next tick so screen readers announce
    // the dialog body before the focus shift.
    const timer = window.setTimeout(() => confirmRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(timer);
    };
  }, [open, onDismiss]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const iconNode = icon ?? (
    <AlertTriangle
      size={16}
      className={cn(tone === 'danger' ? 'text-danger' : 'text-warning')}
      aria-hidden
    />
  );

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      data-testid={testId ?? 'confirm-dialog'}
      className="fixed inset-0 z-50 grid place-items-center bg-black/35 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onDismiss();
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded border border-rule bg-surface p-5 shadow-elev-3"
      >
        <header className="flex items-center gap-2">
          {iconNode}
          <h2 id="confirm-dialog-title" className="text-sm font-semibold text-fg">
            {title}
          </h2>
          <button
            type="button"
            aria-label={t('common.close')}
            onClick={onDismiss}
            className="ml-auto grid h-6 w-6 place-items-center rounded-xs text-fg-subtle hover:bg-surface-2"
          >
            <X size={14} />
          </button>
        </header>
        <p className="mt-3 text-xs text-fg-muted">{body}</p>
        <footer className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onDismiss}>
            {dismissLabel ?? t('common.cancel')}
          </Button>
          <Button
            ref={confirmRef}
            type="button"
            variant={tone === 'danger' ? 'danger' : 'primary'}
            size="sm"
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </footer>
      </div>
    </div>,
    document.body
  );
}

export interface CancelRunDialogProps {
  open: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}

// Preset wrapper for the most common destructive flow on the admin dashboard.
// Pulls every string from admin.confirm.cancelRun.* (DEV-217) so callers don't
// need to duplicate the copy — and a future redesign can swap the body in one
// place.
export function CancelRunDialog({ open, onConfirm, onDismiss }: CancelRunDialogProps) {
  const { t } = useTranslation();
  return (
    <ConfirmDialog
      open={open}
      title={t('admin.confirm.cancelRun.title')}
      body={t('admin.confirm.cancelRun.body')}
      confirmLabel={t('admin.confirm.cancelRun.confirm')}
      dismissLabel={t('admin.confirm.cancelRun.dismiss')}
      tone="danger"
      onConfirm={onConfirm}
      onDismiss={onDismiss}
      data-testid="cancel-run-dialog"
    />
  );
}
